import React from 'react';
import { PORTAL_SWITCH } from './portalTheme';

/** Dist-/Source-Marker: einheitlicher Ein/Aus-Schalter (Kontext, Features). */
export const PORTAL_SWITCH_MARKER = 'portal-switch:ein-aus-v1';

export type PortalSwitchSize = 'sm' | 'md';
export type PortalSwitchAccent = 'violet' | 'sky' | 'emerald';
export type PortalSwitchLayout = 'inline' | 'spread';

const SIZE = {
  sm: {
    track: 'h-4 w-7',
    thumb: 'h-2.5 w-2.5',
    on: 'translate-x-[14px]',
    off: 'translate-x-[3px]',
    label: 'text-[10px]',
  },
  md: {
    track: 'h-5 w-9',
    thumb: 'h-3.5 w-3.5',
    on: 'translate-x-[18px]',
    off: 'translate-x-[3px]',
    label: 'text-xs',
  },
} as const;

export interface PortalSwitchProps {
  checked: boolean;
  onChange: (next: boolean) => void;
  label: string;
  description?: React.ReactNode;
  disabled?: boolean;
  size?: PortalSwitchSize;
  layout?: PortalSwitchLayout;
  accent?: PortalSwitchAccent;
  icon?: React.ReactNode;
  className?: string;
  title?: string;
  'data-testid'?: string;
}

/**
 * Sichtbarer Ein/Aus-Schalter (Track + Knopf).
 * Polar: Kontext/Feature-Bools. Nicht für Pin/Merken-Chips.
 */
export const PortalSwitch: React.FC<PortalSwitchProps> = ({
  checked,
  onChange,
  label,
  description,
  disabled = false,
  size = 'md',
  layout = 'inline',
  accent = 'violet',
  icon,
  className = '',
  title,
  'data-testid': testId,
}) => {
  const dim = SIZE[size];
  const stateWord = checked ? 'ein' : 'aus';
  const isSpread = layout === 'spread';

  const track = (
    <span
      className={`relative inline-flex flex-shrink-0 items-center rounded-full transition-colors ${dim.track} ${
        checked ? PORTAL_SWITCH.trackOn[accent] : PORTAL_SWITCH.trackOff
      } ${checked ? 'ring-1 ring-white/20' : 'ring-1 ring-white/10'}`}
      aria-hidden
    >
      <span
        className={`${PORTAL_SWITCH.thumb} ${dim.thumb} ${checked ? dim.on : dim.off}`}
      />
    </span>
  );

  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={`${label}, ${stateWord}`}
      title={title || `${label} ${checked ? 'ein' : 'aus'}schalten`}
      disabled={disabled}
      data-testid={testId}
      data-portal-switch={PORTAL_SWITCH_MARKER}
      onClick={() => {
        if (!disabled) onChange(!checked);
      }}
      onKeyDown={(event) => {
        if (disabled) return;
        if (event.key === ' ' || event.key === 'Enter') {
          event.preventDefault();
          onChange(!checked);
        }
      }}
      className={`select-none transition-colors focus:outline-none focus-visible:ring-1 focus-visible:ring-violet-400/40 rounded-md ${
        disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer hover:brightness-110'
      } ${
        isSpread
          ? 'w-full flex items-center justify-between gap-3 text-left'
          : 'inline-flex items-center gap-1.5 flex-shrink-0'
      } ${className}`}
    >
      <span className={`flex items-start gap-2 min-w-0 ${isSpread ? '' : 'items-center'}`}>
        {icon}
        <span className="min-w-0">
          <span
            className={`block whitespace-nowrap ${
              isSpread ? 'text-sm text-white' : `${dim.label} text-slate-400`
            }`}
          >
            {label}
          </span>
          {description ? (
            <span className="block text-[11px] text-slate-500 leading-relaxed">{description}</span>
          ) : null}
        </span>
      </span>
      {track}
    </button>
  );
};
